import React from 'react';
import { PortableText } from '@portabletext/react';
import { StyledArticleContentGrid } from '@pages/Article/Styles';
import { marks } from './marks';
import { types } from './types';
import { list } from './list';
import { block } from './block/BlockSerializer';
import { BlockContentContext, selectRenderableBlocks } from './BlockContent.utils';
import { ContentAbovePaywallContainer, PayWalledContentContainer } from './BlockContent.styles';

interface BlockContentProps {
  value?: any;
  context?: BlockContentContext;
  isAccessibleForUser?: boolean;
}

const BlockContent = ({ value, context, isAccessibleForUser }: BlockContentProps) => {
  const components = React.useMemo(
    () => ({
      types: types(context),
      list: list(context),
      block: block(context),
      marks,
    }),
    [context],
  );

  const filteredComponents = React.useMemo(
    () => selectRenderableBlocks(value, Object.keys(components.types)),
    [value, components.types],
  );

  if (!filteredComponents || filteredComponents.length === 0) {
    return null;
  }

  const paywallIndex = filteredComponents.findIndex((e) => e._type === 'paywallReference');
  const hasPaywall = paywallIndex !== -1;

  if (hasPaywall) {
    const abovePaywall = isAccessibleForUser
      ? filteredComponents.slice(0, paywallIndex)
      : filteredComponents.slice(0, paywallIndex + 1);
    const belowPaywall = filteredComponents.slice(paywallIndex + 1);
    return context === BlockContentContext.Article ? (
      <>
        <ContentAbovePaywallContainer>
          <StyledArticleContentGrid>
            <PortableText value={abovePaywall} components={components} />
          </StyledArticleContentGrid>
        </ContentAbovePaywallContainer>
        <PayWalledContentContainer className="paywall" $isAccessibleForUser={isAccessibleForUser}>
          <StyledArticleContentGrid>
            <PortableText value={belowPaywall} components={components} />
          </StyledArticleContentGrid>
        </PayWalledContentContainer>
      </>
    ) : (
      <>
        <ContentAbovePaywallContainer>
          <PortableText value={abovePaywall} components={components} />
        </ContentAbovePaywallContainer>
        <PayWalledContentContainer className="paywall" $isAccessibleForUser={isAccessibleForUser}>
          <PortableText value={belowPaywall} components={components} />
        </PayWalledContentContainer>
      </>
    );
  }

  return context === BlockContentContext.Article ? (
    <StyledArticleContentGrid>
      <PortableText value={filteredComponents} components={components} />
    </StyledArticleContentGrid>
  ) : (
    <PortableText value={filteredComponents} components={components} />
  );
};

export default BlockContent;
